/* A slide, drawn on screen from the SAME shapes the .pptx is written from
 * (utils/deckLayout.js). Her ask, 2026-09-16: "the preview is key". Because
 * neither this nor deckExport decides layout, what she sees here and what she
 * downloads cannot drift apart.
 *
 * Knows nothing about a presentation — it is handed one slide and a resolved
 * skin. Sizes are inches on a 13.333 × 7.5 slide, scaled to the width given;
 * font sizes are points (1pt = 1/72in).
 */
import { SLIDE_H, SLIDE_W, layoutSlide } from "../../utils/deckLayout";

export function SlidePreview({ slide, skin, width = 240, design = "editorial", label }) {
  const k = width / SLIDE_W;
  const { background, shapes } = layoutSlide(slide, skin, design);
  const box = (s) => ({ position: "absolute", left: s.x * k, top: s.y * k, width: s.w * k, height: Math.max(s.h * k, 1) });
  return (
    <div role="img" aria-label={label || "Slide preview"}
      style={{ position: "relative", width, height: SLIDE_H * k, background: `#${background}`, overflow: "hidden", borderRadius: Math.max(2, width / 80), boxShadow: "0 0 0 1px var(--border-subtle, rgba(0,0,0,0.12))", flexShrink: 0 }}>
      {shapes.map((s, i) => {
        if (s.kind === "rect") return <div key={i} style={{ ...box(s), background: `#${s.fill}`, opacity: 1 - (s.transparency || 0) / 100 }} />;
        if (s.kind === "line") return <div key={i} style={{ ...box(s), height: Math.max(1, ((s.weight || 2) * k) / 72), background: `#${s.color}` }} />;
        if (s.kind === "image") {
          const round = s.rounding ? { borderRadius: "50%" } : null;
          return s.placeholder
            ? <div key={i} title={s.alt} style={{ ...box(s), ...round, background: s.fill ? `#${s.fill}` : "rgba(255,255,255,0.18)" }} />
            : <img key={i} src={s.src} alt="" style={{ ...box(s), ...round, objectFit: "cover" }} />;
        }
        if (s.kind === "text") {
          if (!String(s.text || "").trim()) return null;
          const px = (s.fontSize * k) / 72;
          return (
            <div key={i} style={{
              ...box(s), display: "flex", flexDirection: "column", overflow: "hidden",
              justifyContent: s.valign === "middle" ? "center" : s.valign === "bottom" ? "flex-end" : "flex-start",
              textAlign: s.align, color: `#${s.color}`, fontFamily: `"${s.fontFace}", sans-serif`,
              fontSize: px, lineHeight: 1.15, fontWeight: s.bold ? 700 : 400, fontStyle: s.italic ? "italic" : "normal",
              whiteSpace: "pre-wrap", wordBreak: "break-word",
            }}>{s.text}</div>
          );
        }
        return null;
      })}
    </div>
  );
}
